import { IAccountingUser } from '@/interfaces';
import { User, Coins, Briefcase, Calendar, Activity } from 'lucide-react';
import { amountToString } from '@/functions';
import { AccountingUserTypeEnum, AccountingUserStatusEnum, CurrencyEnum } from '@/enums';
import dayjs from 'dayjs';

interface TProps {
    user: IAccountingUser;
}

const ViewAccountingUserDetails = ({ user }: TProps) => {
    const isInvestorBalanceNegative = (user.balance ?? 0) < 0;
    const investorBalanceLabel = user.balance == null
        ? '-'
        : amountToString(user.balance, CurrencyEnum.EUR);

    const DetailItem = ({ label, value, icon: Icon, className = "" }: { label: string, value: React.ReactNode, icon?: any, className?: string }) => (
        <div className={`space-y-1.5 ${className}`}>
            <div className="flex items-center space-x-2 text-muted-foreground/70">
                {Icon && <Icon size={14} />}
                <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
            </div>
            <div className="text-sm font-medium text-foreground">
                {value || '-'}
            </div>
        </div>
    );

    const renderStatus = (status: AccountingUserStatusEnum) => {
        const isActive = status === AccountingUserStatusEnum.ACTIVE;
        return (
            <div className="inline-flex items-center space-x-2">
                <div className={`h-2 w-2 rounded-full ${isActive ? 'bg-green-500 shadow-[0_0_4px_rgba(34,197,94,0.4)]' : 'bg-gray-300'}`} />
                <span>{isActive ? 'Actif' : 'Désactivé'}</span>
            </div>
        );
    };

    return (
        <div className="pt-2 px-3 pb-2 space-y-6">
            {/* Header Section */}
            <div className="flex items-center space-x-3 md:space-x-4">
                <div className="h-12 w-12 md:h-14 md:w-14 rounded-xl md:rounded-2xl bg-linear-to-br from-primary/10 to-primary/5 border border-primary/10 flex items-center justify-center text-primary font-bold text-xl md:text-2xl shadow-xs shrink-0">
                    {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                    <h1 className="text-lg md:text-2xl font-bold text-foreground tracking-tight leading-tight">{user.name}</h1>
                    <div className="flex items-center space-x-2 mt-0.5 md:mt-1">
                        <span className="px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground text-[10px] md:text-xs font-semibold whitespace-nowrap">
                            {user.role}
                        </span>
                    </div>
                </div>

            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-6">

                <DetailItem
                    icon={Calendar}
                    label="Date d'ajout"
                    value={dayjs(user.createdAt).format('DD MMM YYYY')}
                />

                <div className="col-span-2 h-px bg-border/40" />

                <DetailItem
                    icon={User}
                    label="Type"
                    value={
                        <span className="flex items-center">
                            {user.type === AccountingUserTypeEnum.EMPLOYEE ? <Briefcase size={14} className="mr-2 opacity-70" /> : <Activity size={14} className="mr-2 opacity-70" />}
                            {user.type === AccountingUserTypeEnum.EMPLOYEE ? 'Employé' : 'Investisseur'}
                        </span>
                    }
                />
                <DetailItem
                    icon={Activity}
                    label="Statut du compte"
                    value={renderStatus(user.status)}
                />

                <div className="col-span-2 h-px bg-border/40" />

                {user.type === AccountingUserTypeEnum.EMPLOYEE && (
                    <>
                        <DetailItem
                            icon={Calendar}
                            label="Date de début"
                            value={user.startedAt ? dayjs(user.startedAt).format('DD MMM YYYY') : '-'}
                        />
                        {user.status === AccountingUserStatusEnum.DISABLED && user.disabledAt && (
                            <DetailItem
                                icon={Calendar}
                                label="Date de fin"
                                value={dayjs(user.disabledAt).format('DD MMM YYYY')}
                            />
                        )}
                        <div className="col-span-2 h-px bg-border/40" />
                    </>
                )}

                {user.type === AccountingUserTypeEnum.EMPLOYEE ? (
                    <DetailItem
                        icon={Coins}
                        label="Rémunération (Salaire)"
                        className="col-span-2"
                        value={
                            <span className="text-xl md:text-2xl font-bold tracking-tight text-primary break-all">
                                {amountToString(user.baseSalaryEur || 0, CurrencyEnum.EUR)}
                                <span className="text-xs md:text-sm font-normal text-muted-foreground ml-1">/ mois</span>
                            </span>
                        }
                    />
                ) : (
                    <>
                        <DetailItem
                            icon={Coins}
                            label="Participation"
                            value={
                                <span className="text-xl md:text-2xl font-bold tracking-tight text-primary">
                                    {user.percentage}%
                                    <span className="text-xs md:text-sm font-normal text-muted-foreground ml-1">des parts</span>
                                </span>
                            }
                        />
                        <DetailItem
                            icon={Coins}
                            label="Balance"
                            value={
                                <span className={`text-xl md:text-2xl font-bold tracking-tight ${isInvestorBalanceNegative ? 'text-red-600' : 'text-primary'}`}>
                                    {investorBalanceLabel}
                                </span>
                            }
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default ViewAccountingUserDetails;
